import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {
  CartProduct,
  CustomerGroup,
  FileService,
  ListTable,
  ProductCustomer,
  ProductCustomerAmount,
  StoreService,
  Warehouse
} from 'compob2b-libs';
import {APIDefinition, Columns} from 'compob2b-table';
import {filter, map, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../../../environments/translations.ru';
import {ModalProductComponent} from '../../../../product/modal-product/modal-product.component';
import {ProductService} from '../../../../product/services/product.service';
import {AddProductComponent} from '../popup-add-product/add-product.component';
import {AddSimilarProductComponent} from '../popup-similar-product/add-similar-product.component';
import {NbCardModule, NbCheckboxModule} from "@nebular/theme";
import {BaseModule} from "../../../../../../../../compob2b-table/lib/components/base/base.module";
import {ProductPriceComponent} from "../../../../product/product-list/product-price/product-price.component";
import {
  ProductAvailabilityComponent
} from "../../../../product/product-list/product-availability/product-availability.component";
import {SpecificationAddToCartComponent} from "../specification-add-to-cart/specification-add-to-cart.component";
import {NgIf} from "@angular/common";

@Component({
    selector: 'app-import-list',
    templateUrl: './import-list.component.html',
    styleUrls: ['./import-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbCardModule, BaseModule, NbCheckboxModule, ProductAvailabilityComponent, ProductPriceComponent, SpecificationAddToCartComponent, NgIf]
})
export class SpecificationImportListComponent extends ListTable implements OnInit {

  constructor(private readonly fileService: FileService,
              private readonly storeService: StoreService) {
    super();
  }

  @Input() cartProductList: CartProduct[];
  @Output() onDelete = new EventEmitter<number>();
  @Output() onSelect = new EventEmitter<Set<number>>();
  @Output() onReplace = new EventEmitter<{ index: number, product: CartProduct }>();

  readonly customerGroup$ = this.storeService.getCustomerGroup();

  productService = inject(ProductService);

  @ViewChild('table', {static: true}) table: APIDefinition;
  @ViewChild('checkboxTpl', {static: true}) checkboxTpl: TemplateRef<any>;
  @ViewChild('codeIconTpl', {static: true}) codeIconTpl: TemplateRef<any>;
  @ViewChild('photoTpl', {static: true}) photoTpl: TemplateRef<any>;
  @ViewChild('headerTpl', {static: true}) headerTpl: TemplateRef<any>;
  @ViewChild('importKeyTpl', {static: true}) importKeyTpl: TemplateRef<any>;
  @ViewChild('availabilityTpl', {static: true}) availabilityTpl: TemplateRef<any>;
  @ViewChild('priceTpl', {static: true}) priceTpl: TemplateRef<any>;
  @ViewChild('priceVATTpl', {static: true}) priceVATTpl: TemplateRef<any>;
  @ViewChild('cartTpl', {static: true}) cartTpl: TemplateRef<any>;

  private static createCartProduct(productCustomer: ProductCustomer, amount: number): CartProduct {
    return {
      id: 0,
      amount,
      syncUid: productCustomer.syncUid,
      articul: productCustomer.articul,
      importKey: productCustomer.importKey,
      header: productCustomer.header,
      description: productCustomer.description,
      brandHeader: productCustomer.brandHeader,
      manufacturerHeader: productCustomer.manufacturerHeader,
      unitHeader: productCustomer.unitHeader,
      price: productCustomer.price,
      picture: productCustomer.picture,
      productId: productCustomer.id,
      manufacturerSeriesHeader: productCustomer.manufacturerSeriesHeader,
      commercePrice: productCustomer.commercePrice,
      flatCodes: productCustomer.flatCodes,
      balancesOnGroupsOfWarehouses: productCustomer.balancesOnGroupsOfWarehouses,
      markers: productCustomer.markers,
      supplyTerm: productCustomer.supplyTerm,
      multiplicityOrder: productCustomer.multiplicityOrder,
      multiplicitySupplier: productCustomer.multiplicitySupplier,
      selected: true,
      productStatusId: productCustomer.productStatusId,
      similarProductList: null,
      sync: productCustomer.sync,
      productCode: productCustomer.productCode
    };
  }

  ngOnInit(): void {
    this.initTableConfiguration();

    this.customerGroup$.pipe(tap(data => this.handleCustomerGroup(data)), takeUntil(this.unsubscribe)).subscribe();
  }

  private selectAll() {
    this.resetSelected();
    for (let i = 0; i < this.cartProductList.length; i++) {
      this.selected.add(i);
    }
    this.onSelect.emit(this.selected);
  }

  openModalProduct(syncUid: string): void {
    this.productService.openModalProduct(ModalProductComponent, syncUid);
  }

  protected initTableConfiguration(): void {
    this.loading = true;
    this.configuration.paginationEnabled = false;
    this.configuration.orderEnabled = false;
  }

  private handleCustomerGroup(customerGroup: CustomerGroup) {
    this.productService.handleCustomerGroup(customerGroup);
    if (customerGroup) {
      this.columns = this.initTableColumns(customerGroup.warehouseGroup);
      this.selectAll();
    }
    this.loading = false;
    this.cdr.detectChanges();
  }

  protected initTableColumns(warehouses: Warehouse[]): Columns[] {
    const columns: Columns[] = [];

    columns.push({key: 'checkbox', title: '', cellTemplate: this.checkboxTpl});
    columns.push({key: 'productCode', title: 'Код товара', cellTemplate: this.importKeyTpl});
    columns.push({key: 'photo', title: translations.photo, cellTemplate: this.photoTpl});
    columns.push({key: 'header', title: translations.articulHeaderShowcase, cellTemplate: this.headerTpl});

    if (warehouses?.length) {
      columns.push({
        key: 'availability',
        title: translations.availability,
        cellTemplate: this.availabilityTpl
      });
    }

    columns.push({key: 'price', title: translations.priceRur, cellTemplate: this.priceTpl});
    columns.push({key: 'priceVAT', title: translations.priceNdsRur, cellTemplate: this.priceVATTpl});
    columns.push({key: 'to_cart', title: translations.amount, cellTemplate: this.cartTpl});

    return columns;
  }

  public getImageUri(picture: string) {
    return picture ? this.fileService.getImageSrc(picture, 800) : this.noPhotoSrc;
  }

  delete(index: number): void {
    this.onDelete.emit(index);
  }

  toggle(index: number) {
    if (this.selected.has(index)) {
      this.selected.delete(index);
    } else {
      this.selected.add(index);
    }

    this.onSelect.emit(this.selected);
  }

  openSearchProduct(index: number) {
    this.windowService.open(AddProductComponent, {
      windowClass: 'import-popup',
    }).onClose.pipe(
      filter((data: ProductCustomerAmount) => data !== undefined),
      map((data: ProductCustomerAmount) => SpecificationImportListComponent.createCartProduct(data.product, data.amount)),
      tap((data: CartProduct) => this.replaceProduct(index, data)),
      takeUntil(this.unsubscribe),
    ).subscribe();
  }

  replaceProduct(index: number, product: CartProduct) {
    this.onReplace.emit({index, product});
  }

  openSimilarProducts(rowIndex: number, similarProductList: CartProduct[], importKey: string) {
    this.windowService.open(AddSimilarProductComponent, {
      context: {cartProductList: similarProductList, requestedImportKey: importKey},
      windowClass: 'import-popup'
    }).onClose.pipe(
      filter((data: any) => data !== undefined),
      map((data: any) => ('product' in data) ? SpecificationImportListComponent.createCartProduct(data.product, data.amount) : data),
      tap((data: CartProduct) => this.replaceProduct(rowIndex, data)),
      takeUntil(this.unsubscribe),
    ).subscribe();
  }
}

